import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Animated,
  Dimensions,
  DimensionValue,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../hooks/useTheme';
import UserAvatar from '../../shared/UserAvatar';
import {
  MoodBoardCardItem,
  MoodBoardCardActions,
  ImageNavigationProps,
} from './types';

interface VerticalMoodboardCardProps {
  item: MoodBoardCardItem;
  actions: MoodBoardCardActions;
  imageNavigation: ImageNavigationProps;
  showEditButton?: boolean;
  currentUserId?: string;
  animatedStyle?: {
    opacity?: Animated.Value;
    transform?: Array<{ scale?: Animated.Value }>;
  };
}

const VerticalMoodboardCard: React.FC<VerticalMoodboardCardProps> = ({
  item,
  actions,
  imageNavigation,
  showEditButton = false,
  currentUserId,
  animatedStyle,
}) => {
  const { colors } = useTheme();
  const screenWidth = Dimensions.get('window').width;
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;
  const isHorizontal = !isMobile && Platform.OS === 'web' && screenWidth >= 768;
  const isDesktop = Platform.OS === 'web' && screenWidth >= 768;
  const currentImage =
    item.images && item.images.length > 0
      ? item.images[imageNavigation.currentIndex]
      : null;
  const hasMultipleImages = item.images && item.images.length > 1;
  const shouldShowChevrons = hasMultipleImages;

  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
    aspectRatio: number;
  } | null>(null);

  const getDefaultImageStyle = () => {
    const baseWidth =
      screenWidth < 768 ? screenWidth * 0.95 : Math.min(500, screenWidth * 0.9);
    return { width: baseWidth, height: baseWidth }; // Square default (1:1 aspect ratio)
  };
  const [imageStyle, setImageStyle] = useState<{
    width: number;
    height: number;
  }>(getDefaultImageStyle());

  useEffect(() => {
    if (currentImage && Platform.OS === 'web') {
      const img = new window.Image();
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const aspectRatio = width / height;
        const baseWidth = Math.min(500, screenWidth * 0.9);

        let finalWidth: number;
        let finalHeight: number;

        if (aspectRatio >= 0.99 && aspectRatio <= 1.01) {
          finalWidth = baseWidth;
          finalHeight = baseWidth;
        } else if (aspectRatio < 0.99) {
          const maxHeight = baseWidth * (5 / 4);
          finalWidth = baseWidth;
          finalHeight = Math.min(baseWidth / aspectRatio, maxHeight);
        } else {
          const maxAspectRatio = 1.91;
          const maxHeight = baseWidth / maxAspectRatio;
          finalWidth = baseWidth;
          finalHeight = Math.max(baseWidth / aspectRatio, maxHeight);
        }

        setImageDimensions({ width, height, aspectRatio });
        setImageStyle({ width: finalWidth, height: finalHeight });
      };
      img.onerror = () => {
        const fallbackWidth = Math.min(500, screenWidth * 0.9);
        setImageDimensions({
          width: fallbackWidth,
          height: fallbackWidth,
          aspectRatio: 1,
        });
        setImageStyle({ width: fallbackWidth, height: fallbackWidth });
      };
      img.src = currentImage;
    } else if (currentImage) {
      Image.getSize(
        currentImage,
        (width, height) => {
          const aspectRatio = width / height;
          const baseWidth =
            screenWidth < 768
              ? screenWidth * 0.95
              : Math.min(500, screenWidth * 0.9);

          let finalWidth: number;
          let finalHeight: number;

          if (aspectRatio >= 0.99 && aspectRatio <= 1.01) {
            finalWidth = baseWidth;
            finalHeight = baseWidth;
          } else if (aspectRatio < 0.99) {
            const maxHeight = baseWidth * (5 / 4);
            finalWidth = baseWidth;
            finalHeight = Math.min(baseWidth / aspectRatio, maxHeight);
          } else {
            const maxAspectRatio = 1.91;
            const maxHeight = baseWidth / maxAspectRatio;
            finalWidth = baseWidth;
            finalHeight = Math.max(baseWidth / aspectRatio, maxHeight);
          }

          setImageDimensions({ width, height, aspectRatio });
          setImageStyle({ width: finalWidth, height: finalHeight });
        },
        () => {
          const baseWidth =
            screenWidth < 768
              ? screenWidth * 0.95
              : Math.min(500, screenWidth * 0.9);
          setImageDimensions({ width: 300, height: 300, aspectRatio: 1 });
          setImageStyle({ width: baseWidth, height: baseWidth });
        }
      );
    } else {
      setImageDimensions(null);
      const baseWidth =
        screenWidth < 768
          ? screenWidth * 0.95
          : Math.min(500, screenWidth * 0.9);
      setImageStyle({ width: baseWidth, height: baseWidth });
    }
  }, [currentImage, screenWidth]);

  const handlePress = () => {
    if (actions.onPress) {
      actions.onPress(item.id);
    }
  };

  const handleLike = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onLike) {
      actions.onLike(item.id);
    }
  };

  const handleShare = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onShare) {
      actions.onShare(item);
    }
  };

  const handleSave = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onSave) {
      actions.onSave(item.id);
    }
  };

  const handleEdit = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onEdit) {
      actions.onEdit(item.id);
    }
  };

  const handleReport = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onReport) {
      actions.onReport(item.id);
    }
  };

  if (animatedStyle) {
    return (
      <Animated.View style={[styles.container, animatedStyle as any]}>
        <View
          style={[
            styles.card,
            { backgroundColor: colors.card },
            item.isSaved && styles.cardSaved,
          ]}
        >
          <TouchableOpacity
            style={styles.cardTouchable}
            onPress={handlePress}
            activeOpacity={0.95}
          >
            {currentImage ? (
              <View
                style={[
                  styles.imageSectionVertical,
                  { width: imageStyle.width, height: imageStyle.height },
                ]}
              >
                <View
                  style={[
                    styles.imageContainer,
                    { width: imageStyle.width, height: imageStyle.height },
                  ]}
                >
                  <Image
                    source={{ uri: currentImage }}
                    style={[
                      styles.singleImage,
                      { width: imageStyle.width, height: imageStyle.height },
                    ]}
                    resizeMode="cover"
                  />

                  {/* Like indicator overlay */}
                  {item.isLiked && (
                    <View style={styles.likedBadge}>
                      <Ionicons name="heart" size={16} color="#FF6B6B" />
                    </View>
                  )}

                  {/* Show chevrons when there are multiple images */}
                  {shouldShowChevrons && (
                    <>
                      <TouchableOpacity
                        style={styles.navButton}
                        onPress={(e) => {
                          e.stopPropagation();
                          imageNavigation.onPrev();
                        }}
                        activeOpacity={0.7}
                      >
                        <View style={styles.navButtonIconWrapper}>
                          <Ionicons
                            name="chevron-back"
                            size={24}
                            color="white"
                          />
                        </View>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.navButton, styles.navButtonRight]}
                        onPress={(e) => {
                          e.stopPropagation();
                          imageNavigation.onNext();
                        }}
                        activeOpacity={0.7}
                      >
                        <View style={styles.navButtonIconWrapper}>
                          <Ionicons
                            name="chevron-forward"
                            size={24}
                            color="white"
                          />
                        </View>
                      </TouchableOpacity>
                      {imageNavigation.showCount && (
                        <View style={styles.imageCountBadge}>
                          <Text style={styles.imageCountText}>
                            {imageNavigation.currentIndex + 1}/
                            {imageNavigation.totalImages}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                </View>
              </View>
            ) : (
              <View
                style={[
                  styles.imageSectionVertical,
                  styles.emptyImagePlaceholder,
                ]}
              >
                <Ionicons
                  name="image-outline"
                  size={48}
                  color={colors.secondary}
                />
              </View>
            )}
          </TouchableOpacity>

          {/* Moodboard Details Below Image */}
          <View
            style={[
              styles.detailsSection,
              imageStyle
                ? { width: imageStyle.width, minWidth: imageStyle.width }
                : {},
            ]}
          >
            {/* Header with Username, Privacy Badge, and Edit Button */}
            <View style={styles.detailsHeader}>
              <View style={styles.headerLeft}>
                <View style={styles.userInfoSection}>
                  <UserAvatar
                    size={32}
                    imageUrl={item.userAvatar}
                    name={item.username}
                    email={item.userEmail}
                    showActiveStatus={true}
                    isActive={true}
                  />
                  <View style={styles.userTextInfo}>
                    <Text
                      style={[styles.username, { color: colors.text }]}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {item.username}
                    </Text>
                    {item.timestamp && (
                      <Text
                        style={[styles.timestamp, { color: colors.secondary }]}
                        numberOfLines={1}
                      >
                        {item.timestamp}
                      </Text>
                    )}
                  </View>
                </View>
                <View
                  style={[
                    styles.privacyBadge,
                    {
                      backgroundColor: item.is_public
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'rgba(107, 114, 128, 0.1)',
                    },
                  ]}
                >
                  <Ionicons
                    name={item.is_public ? 'globe-outline' : 'lock-closed'}
                    size={10}
                    color={item.is_public ? '#22C55E' : '#6B7280'}
                  />
                  <Text
                    style={[
                      styles.privacyText,
                      { color: item.is_public ? '#22C55E' : '#6B7280' },
                    ]}
                  >
                    {item.is_public ? 'Public' : 'Private'}
                  </Text>
                </View>
              </View>
              {/* Edit Button - Only show for user's own stories */}
              {showEditButton &&
                currentUserId &&
                item.userId === parseInt(currentUserId) && (
                  <TouchableOpacity
                    style={styles.editButton}
                    onPress={handleEdit}
                    activeOpacity={0.7}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons
                      name="create-outline"
                      size={18}
                      color={colors.secondary}
                    />
                  </TouchableOpacity>
                )}
            </View>

            {/* Title */}
            <Text
              style={[styles.title, { color: colors.text }]}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {item.title}
            </Text>

            {/* Description */}
            {item.description && (
              <Text
                style={[styles.description, { color: colors.secondary }]}
                numberOfLines={3}
                ellipsizeMode="tail"
              >
                {item.description}
              </Text>
            )}

            {/* Location */}
            {item.location && (
              <View style={styles.locationRow}>
                <Ionicons
                  name="location-outline"
                  size={12}
                  color={colors.secondary}
                />
                <Text
                  style={[styles.locationText, { color: colors.secondary }]}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {item.location}
                </Text>
              </View>
            )}

            {/* Stats */}
            <Text style={[styles.stats, { color: colors.secondary }]}>
              {item.likes || 0} likes • {item.shares || 0} shares •{' '}
              {item.saved || 0} saved
            </Text>

            {/* Tags - Always show section */}
            {item.tags && item.tags.length > 0 && (
              <View style={styles.tagsContainer}>
                {item.tags.map((tag: string, tagIndex: number) => (
                  <View
                    key={tagIndex}
                    style={[
                      styles.tag,
                      { backgroundColor: colors.primary + '15' },
                    ]}
                  >
                    <Text style={[styles.tagText, { color: colors.primary }]}>
                      #{tag}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* Colors - Always show section */}
            {item.colors && item.colors.length > 0 && (
              <View style={styles.colorsContainer}>
                <View style={styles.colorPalette}>
                  {item.colors.map((color: string, colorIndex: number) => (
                    <View
                      key={colorIndex}
                      style={[styles.colorCircle, { backgroundColor: color }]}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* Action Buttons - Always visible */}
            <View style={styles.actionsRow}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleLike}
                activeOpacity={0.7}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name={item.isLiked ? 'heart' : 'heart-outline'}
                  size={20}
                  color={item.isLiked ? '#FF6B6B' : colors.secondary}
                />
                <Text
                  style={[
                    styles.actionText,
                    { color: item.isLiked ? '#FF6B6B' : colors.secondary },
                  ]}
                >
                  Like
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleShare}
                activeOpacity={0.7}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name="share-outline"
                  size={20}
                  color={colors.secondary}
                />
                <Text style={[styles.actionText, { color: colors.secondary }]}>
                  Share
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleSave}
                activeOpacity={0.7}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name={item.isSaved ? 'bookmark' : 'bookmark-outline'}
                  size={20}
                  color={item.isSaved ? '#FFA500' : colors.secondary}
                />
                <Text
                  style={[
                    styles.actionText,
                    { color: item.isSaved ? '#FFA500' : colors.secondary },
                  ]}
                >
                  {item.isSaved ? 'Saved' : 'Save'}
                </Text>
              </TouchableOpacity>

              {actions.onReport &&
                currentUserId &&
                item.userId !== parseInt(currentUserId) && (
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={handleReport}
                    activeOpacity={0.7}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons
                      name="flag-outline"
                      size={20}
                      color={colors.secondary}
                    />
                    <Text
                      style={[styles.actionText, { color: colors.secondary }]}
                    >
                      Report
                    </Text>
                  </TouchableOpacity>
                )}
            </View>
          </View>
        </View>
      </Animated.View>
    );
  }

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.card,
          { backgroundColor: colors.card },
          item.isSaved && styles.cardSaved,
        ]}
      >
        <TouchableOpacity
          style={styles.cardTouchable}
          onPress={handlePress}
          activeOpacity={0.95}
        >
          {/* Instagram-style: Just the image */}
          {currentImage ? (
            <View
              style={[
                styles.imageSectionVertical,
                { width: imageStyle.width, height: imageStyle.height },
              ]}
            >
              <View
                style={[
                  styles.imageContainer,
                  { width: imageStyle.width, height: imageStyle.height },
                ]}
              >
                <Image
                  source={{ uri: currentImage }}
                  style={[
                    styles.singleImage,
                    { width: imageStyle.width, height: imageStyle.height },
                  ]}
                  resizeMode="cover"
                />

                {/* Like indicator overlay */}
                {item.isLiked && (
                  <View style={styles.likedBadge}>
                    <Ionicons name="heart" size={16} color="#FF6B6B" />
                  </View>
                )}

                {/* Show chevrons when there are multiple images */}
                {shouldShowChevrons && (
                  <>
                    <TouchableOpacity
                      style={styles.navButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        imageNavigation.onPrev();
                      }}
                      activeOpacity={0.7}
                    >
                      <View style={styles.navButtonIconWrapper}>
                        <Ionicons name="chevron-back" size={24} color="white" />
                      </View>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.navButton, styles.navButtonRight]}
                      onPress={(e) => {
                        e.stopPropagation();
                        imageNavigation.onNext();
                      }}
                      activeOpacity={0.7}
                    >
                      <View style={styles.navButtonIconWrapper}>
                        <Ionicons
                          name="chevron-forward"
                          size={24}
                          color="white"
                        />
                      </View>
                    </TouchableOpacity>
                    {imageNavigation.showCount && (
                      <View style={styles.imageCountBadge}>
                        <Text style={styles.imageCountText}>
                          {imageNavigation.currentIndex + 1}/
                          {imageNavigation.totalImages}
                        </Text>
                      </View>
                    )}
                  </>
                )}
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.imageSectionVertical,
                styles.emptyImagePlaceholder,
              ]}
            >
              <Ionicons
                name="image-outline"
                size={48}
                color={colors.secondary}
              />
            </View>
          )}
        </TouchableOpacity>

        {/* Moodboard Details Below Image */}
        <View
          style={[
            styles.detailsSection,
            imageStyle
              ? { width: imageStyle.width, minWidth: imageStyle.width }
              : {},
          ]}
        >
          {/* Header with Username, Privacy Badge, and Edit Button */}
          <View style={styles.detailsHeader}>
            <View style={styles.headerLeft}>
              <View style={styles.userInfoSection}>
                <UserAvatar
                  size={32}
                  imageUrl={item.userAvatar}
                  name={item.username}
                  email={item.userEmail}
                  showActiveStatus={true}
                  isActive={true}
                />
                <View style={styles.userTextInfo}>
                  <Text
                    style={[styles.username, { color: colors.text }]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {item.username}
                  </Text>
                  {item.timestamp && (
                    <Text
                      style={[styles.timestamp, { color: colors.secondary }]}
                      numberOfLines={1}
                    >
                      {item.timestamp}
                    </Text>
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.privacyBadge,
                  {
                    backgroundColor: item.is_public
                      ? 'rgba(34, 197, 94, 0.1)'
                      : 'rgba(107, 114, 128, 0.1)',
                  },
                ]}
              >
                <Ionicons
                  name={item.is_public ? 'globe-outline' : 'lock-closed'}
                  size={10}
                  color={item.is_public ? '#22C55E' : '#6B7280'}
                />
                <Text
                  style={[
                    styles.privacyText,
                    { color: item.is_public ? '#22C55E' : '#6B7280' },
                  ]}
                >
                  {item.is_public ? 'Public' : 'Private'}
                </Text>
              </View>
            </View>
            {/* Edit Button - Only show for user's own stories */}
            {showEditButton &&
              currentUserId &&
              item.userId === parseInt(currentUserId) && (
                <TouchableOpacity
                  style={styles.editButton}
                  onPress={handleEdit}
                  activeOpacity={0.7}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons
                    name="create-outline"
                    size={18}
                    color={colors.secondary}
                  />
                </TouchableOpacity>
              )}
          </View>

          {/* Title */}
          <Text
            style={[styles.title, { color: colors.text }]}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {item.title}
          </Text>

          {/* Description */}
          {item.description && (
            <Text
              style={[styles.description, { color: colors.secondary }]}
              numberOfLines={3}
              ellipsizeMode="tail"
            >
              {item.description}
            </Text>
          )}

          {/* Location */}
          {item.location && (
            <View style={styles.locationRow}>
              <Ionicons
                name="location-outline"
                size={12}
                color={colors.secondary}
              />
              <Text
                style={[styles.locationText, { color: colors.secondary }]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {item.location}
              </Text>
            </View>
          )}

          {/* Stats */}
          <Text style={[styles.stats, { color: colors.secondary }]}>
            {item.likes || 0} likes • {item.shares || 0} shares •{' '}
            {item.saved || 0} saved
          </Text>

          {/* Tags - Always show section */}
          {item.tags && item.tags.length > 0 && (
            <View style={styles.tagsContainer}>
              {item.tags.map((tag: string, tagIndex: number) => (
                <View
                  key={tagIndex}
                  style={[
                    styles.tag,
                    { backgroundColor: colors.primary + '15' },
                  ]}
                >
                  <Text style={[styles.tagText, { color: colors.primary }]}>
                    #{tag}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Colors - Always show section */}
          {item.colors && item.colors.length > 0 && (
            <View style={styles.colorsContainer}>
              <View style={styles.colorPalette}>
                {item.colors.map((color: string, colorIndex: number) => (
                  <View
                    key={colorIndex}
                    style={[styles.colorCircle, { backgroundColor: color }]}
                  />
                ))}
              </View>
            </View>
          )}

          {/* Action Buttons - Always visible */}
          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={handleLike}
              activeOpacity={0.7}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name={item.isLiked ? 'heart' : 'heart-outline'}
                size={20}
                color={item.isLiked ? '#FF6B6B' : colors.secondary}
              />
              <Text
                style={[
                  styles.actionText,
                  { color: item.isLiked ? '#FF6B6B' : colors.secondary },
                ]}
              >
                Like
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={handleShare}
              activeOpacity={0.7}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name="share-outline"
                size={20}
                color={colors.secondary}
              />
              <Text style={[styles.actionText, { color: colors.secondary }]}>
                Share
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={handleSave}
              activeOpacity={0.7}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name={item.isSaved ? 'bookmark' : 'bookmark-outline'}
                size={20}
                color={item.isSaved ? '#FFA500' : colors.secondary}
              />
              <Text
                style={[
                  styles.actionText,
                  { color: item.isSaved ? '#FFA500' : colors.secondary },
                ]}
              >
                {item.isSaved ? 'Saved' : 'Save'}
              </Text>
            </TouchableOpacity>

            {actions.onReport &&
              currentUserId &&
              item.userId !== parseInt(currentUserId) && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={handleReport}
                  activeOpacity={0.7}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons
                    name="flag-outline"
                    size={20}
                    color={colors.secondary}
                  />
                  <Text
                    style={[styles.actionText, { color: colors.secondary }]}
                  >
                    Report
                  </Text>
                </TouchableOpacity>
              )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: Platform.OS === 'web' ? 2 : 0,
    marginHorizontal: Platform.OS === 'web' ? 'auto' : 0,
    width: Platform.OS === 'web' ? 'auto' : '100%',
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  card: {
    borderRadius: Platform.OS === 'web' ? 12 : 8,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
    overflow: 'hidden',
    width: Platform.OS === 'web' ? 'auto' : '100%',
    paddingBottom: 0,
    marginBottom: 0,
    flexShrink: 0,
    backgroundColor: 'transparent',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardTouchable: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cardSaved: {
    borderWidth: 2,
    borderColor: '#FFA500',
    borderStyle: 'solid',
  },
  imageSectionVertical: {
    width: Platform.OS === 'web' ? 'auto' : '100%',
    maxWidth: (Platform.OS === 'web' ? '90vw' : '100%') as DimensionValue,
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000000',
    marginBottom: 0,
    paddingBottom: 0,
    marginHorizontal: 0,
    paddingHorizontal: 0,
  },
  horizontalLayout: {
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    minHeight: 200,
    width: Platform.OS === 'web' ? 'auto' : '100%',
    minWidth: Platform.OS === 'web' ? 550 : '100%',
    flexShrink: 0,
    gap: 0,
    overflow: 'visible',
  },
  imageSection: {
    padding: 0,
    paddingBottom: 0,
    marginBottom: 0,
    width: Platform.OS === 'web' ? 'auto' : '100%',
    minWidth: Platform.OS === 'web' ? 0 : '100%',
    maxWidth: Platform.OS === 'web' ? undefined : '100%',
    alignSelf: 'flex-start',
    alignContent: 'stretch',
    flexShrink: 0,
    height: Platform.OS === 'web' ? 'auto' : '100%',
  },
  imageSectionHorizontal: {
    flex: 0,
    flexShrink: 0,
    flexGrow: 0,
    paddingHorizontal: 0,
    paddingVertical: 0,
    paddingBottom: 0,
    paddingTop: 0,
    marginBottom: 0,
    marginTop: 0,
    marginRight: 0,
    alignSelf: 'flex-start',
    overflow: 'visible',
    position: 'relative',
  },
  imageContainer: {
    width: Platform.OS === 'web' ? 'auto' : '100%',
    height: Platform.OS === 'web' ? 'auto' : undefined,
    borderRadius: 0,
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000000',
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
    marginHorizontal: 0,
    paddingHorizontal: 0,
  },
  imageContainerHorizontal: {
    height: 200,
    flexDirection: 'row',
    gap: 4,
  },
  singleImage: {
    width: Platform.OS === 'web' ? 'auto' : '100%',
    height: Platform.OS === 'web' ? 'auto' : undefined,
    flexShrink: 0,
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
    marginHorizontal: 0,
    paddingHorizontal: 0,
  },
  horizontalImageItem: {
    flex: 1,
    minWidth: 120,
    borderRadius: 8,
    overflow: 'hidden',
  },
  horizontalImage: {
    width: '100%',
    height: '100%',
  },
  emptyImagePlaceholder: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  navButton: {
    position: 'absolute',
    left: Platform.OS === 'web' ? 12 : 8,
    top: '50%',
    marginTop: Platform.OS === 'web' ? -24 : -20,
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: Platform.OS === 'web' ? 10 : 8,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  navButtonRight: {
    left: 'auto',
    right: Platform.OS === 'web' ? 12 : 8,
  },
  navButtonIconWrapper: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 20,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.5,
    shadowRadius: 3,
    elevation: 5,
  },
  imageCountBadge: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    zIndex: 10,
  },
  imageCountText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  likedBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 12,
    padding: 4,
    zIndex: 10,
  },
  detailsSection: {
    width: Platform.OS === 'web' ? 'auto' : '100%',
    maxWidth: (Platform.OS === 'web' ? '90vw' : '100%') as DimensionValue,
    padding: Platform.OS === 'web' ? 8 : 0,
    paddingTop: Platform.OS === 'web' ? 8 : 12,
    paddingBottom: Platform.OS === 'web' ? 8 : 12,
    paddingHorizontal: Platform.OS === 'web' ? 8 : 12,
    alignSelf: 'center',
    backgroundColor: 'transparent',
    alignItems: 'stretch',
    marginTop: 0,
  },
  detailsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    marginTop: 0,
    gap: 6,
    width: '100%',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  userInfoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  userTextInfo: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    flex: 1,
  },
  timestamp: {
    fontSize: 10,
    opacity: 0.6,
  },
  editButton: {
    padding: 4,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentSection: {
    width: Platform.OS === 'web' ? '100%' : '100%',
    minWidth: Platform.OS === 'web' ? 300 : '100%',
    padding: 12,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 12,
    overflow: 'visible',
    flexDirection: 'column',
    alignSelf: 'stretch',
  },
  contentScroll: {
    width: '100%',
    gap: 8,
    marginBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
    gap: 8,
  },
  userName: {
    fontSize: Platform.OS === 'web' ? 12 : 14,
    fontWeight: '600',
    flex: 1,
  },
  title: {
    fontSize: Platform.OS === 'web' ? 15 : 17,
    fontWeight: '700',
    marginBottom: Platform.OS === 'web' ? 6 : 8,
    marginTop: 0,
    lineHeight: Platform.OS === 'web' ? 18 : 22,
    width: '100%',
  },
  description: {
    fontSize: Platform.OS === 'web' ? 12 : 14,
    lineHeight: Platform.OS === 'web' ? 16 : 20,
    opacity: 0.75,
    marginBottom: Platform.OS === 'web' ? 8 : 10,
    width: '100%',
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
    width: '100%',
  },
  locationText: {
    fontSize: 11,
    opacity: 0.7,
    flex: 1,
  },
  stats: {
    fontSize: 11,
    opacity: 0.7,
    marginBottom: 8,
    width: '100%',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 6,
    width: '100%',
    alignItems: 'flex-start',
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  tagText: {
    fontSize: 10,
    fontWeight: '500',
  },
  colorsContainer: {
    marginBottom: 8,
    width: '100%',
    alignItems: 'flex-start',
  },
  colorPalette: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  colorCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingTop: 8,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
    marginTop: 6,
    flexShrink: 0,
    width: '100%',
    minHeight: 40,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    gap: 4,
    flex: 1,
    justifyContent: 'center',
  },
  actionText: {
    fontSize: 11,
    fontWeight: '500',
  },
  emptyText: {
    fontSize: 10,
    opacity: 0.5,
    fontStyle: 'italic',
  },
  contentSectionVertical: {
    paddingTop: 16,
    paddingBottom: 16,
    paddingHorizontal: 16,
    paddingRight: 16,
    width: '100%',
    maxWidth: '100%',
    minWidth: Platform.OS === 'web' ? 0 : '100%',
    flex: 1,
    flexShrink: 0,
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 0,
    paddingBottom: 12,
    paddingHorizontal: 0,
    flexWrap: 'wrap',
    gap: 6,
    width: '100%',
    marginBottom: 8,
  },
  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  userInfo: {
    marginLeft: 6,
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  username: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  privacyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 10,
    gap: 3,
  },
  privacyText: {
    fontSize: 9,
    fontWeight: '600',
  },
  bottomSection: {
    marginTop: 'auto',
    flexShrink: 0,
    paddingTop: 8,
    paddingBottom: 0,
    marginBottom: 0,
  },
  colorsSection: {
    marginBottom: 0,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  statsSection: {
    marginBottom: 8,
    paddingBottom: 8,
  },
  statsText: {
    fontSize: Platform.OS === 'web' ? 11 : 12,
    fontWeight: '500',
    opacity: 0.8,
  },
  actionsSectionInline: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingTop: 12,
    paddingBottom: 0,
    marginBottom: 0,
    flexWrap: 'nowrap',
    flexShrink: 0,
    width: '100%',
  },
  actionsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    gap: Platform.OS === 'web' ? 16 : 20,
    marginTop: Platform.OS === 'web' ? 8 : 0,
    paddingTop: 12,
    paddingHorizontal: Platform.OS === 'web' ? 16 : 12,
    paddingBottom: Platform.OS === 'web' ? 0 : 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
    flexWrap: 'nowrap',
    width: '100%',
    maxWidth: '100%',
  },
});

export default VerticalMoodboardCard;
